import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { buildAnalyticsMetricDisplay } from './optionsAnalyticsUtils';
import { formatPercent, formatSignedUsd, formatSignedNative, getCurrencyDigits, getValueTone } from './optionsPerformanceUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { buildAnalyticsMetricInfoMap } from './optionsAnalyticsMetricInfo';
import { OptionsAnalyticsMetricInfoDialog } from './OptionsAnalyticsMetricInfoDialog';

export const OptionsAnalyticsOverview = ({ overview, performanceQuality, selectedCurrency }) => {
  const { t, formatMessage } = useOptionsLocale();
  if (!overview) return null;

  const metrics = performanceQuality?.metrics;
  const metricInfoMap = buildAnalyticsMetricInfoMap({ metrics, performanceQuality, selectedCurrency, t, formatMessage });

  const drawdownPctDisplay = performanceQuality?.max_drawdown_pct === null || performanceQuality?.max_drawdown_pct === undefined
    ? null
    : formatPercent(performanceQuality.max_drawdown_pct);

  const maxDrawdownDisplay = selectedCurrency === 'usd'
    ? {
      primary: formatSignedUsd(performanceQuality?.max_drawdown_usd),
      secondary: drawdownPctDisplay,
      toneClass: getValueTone(performanceQuality?.max_drawdown_usd),
    }
    : {
      primary: performanceQuality?.max_drawdown_native === null || performanceQuality?.max_drawdown_native === undefined
        ? '—'
        : formatSignedNative(performanceQuality.max_drawdown_native, selectedCurrency.toUpperCase(), getCurrencyDigits(selectedCurrency.toUpperCase())),
      secondary: [
        drawdownPctDisplay,
        performanceQuality?.max_drawdown_usd !== null && performanceQuality?.max_drawdown_usd !== undefined ? `≈ ${formatSignedUsd(performanceQuality.max_drawdown_usd)}` : null,
      ].filter(Boolean).join(' · ') || null,
      toneClass: getValueTone(performanceQuality?.max_drawdown_native),
    };

  const cards = [
    { key: 'total', label: t('analytics.totalOperations'), primary: String(overview.total_operations || 0), toneClass: 'text-white', secondary: null, info: null },
    { key: 'win-rate', label: t('analytics.winRate'), ...buildAnalyticsMetricDisplay({ metrics: overview.metrics, selectedCurrency, metricKey: 'winRate' }), info: null },
    { key: 'trading-pnl', label: t('analytics.tradingPnl'), ...buildAnalyticsMetricDisplay({ metrics: overview.metrics, selectedCurrency, metricKey: 'pnl' }), info: null },
    { key: 'fees', label: t('dashboard.fees'), ...buildAnalyticsMetricDisplay({ metrics: overview.metrics, selectedCurrency, metricKey: 'fees' }), info: null },
    { key: 'avg-pnl', label: t('analytics.averagePnl'), ...buildAnalyticsMetricDisplay({ metrics: overview.metrics, selectedCurrency, metricKey: 'avgPnl' }), info: null },
    { key: 'max-drawdown', label: t('analytics.maxDrawdown'), ...maxDrawdownDisplay, info: metricInfoMap.maxDrawdown },
  ];

  return (
    <section className="space-y-3" data-testid="options-analytics-overview-section">
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45" data-typography="label" data-testid="options-analytics-overview-title">{t('analytics.summary')}</p>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="options-analytics-overview-grid">
        {cards.map((card) => (
          <Card key={card.key} className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid={`options-analytics-overview-card-${card.key}`}>
            <CardContent className="px-4 py-4">
              <div className="flex items-center gap-2">
                <p className="text-[11px] uppercase tracking-[0.18em] text-white/38" data-typography="label" data-testid={`options-analytics-overview-card-${card.key}-label`}>{card.label}</p>
                {card.info ? <OptionsAnalyticsMetricInfoDialog info={card.info} testId={`options-analytics-overview-card-${card.key}-dialog`} /> : null}
              </div>
              <p className={`mt-2 text-2xl font-black tracking-tight ${card.toneClass}`} data-typography="metric" data-testid={`options-analytics-overview-card-${card.key}-value`}>{card.primary}</p>
              {card.secondary ? <p className="mt-1 text-xs text-white/52 tabular-nums" data-testid={`options-analytics-overview-card-${card.key}-secondary`}>{card.secondary}</p> : null}
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default OptionsAnalyticsOverview;
